import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const money = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (k) => new Date(`${k}-01T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" });

const PLANS = [
  ["monthly", "Monthly", "#FFAE3D"], ["annual", "Annual", "#EF8200"],
  ["family", "Family", "#8B5E34"], ["gift", "Gifts", "#4F8A5B"],
];

export function RevenueChart({ series, planTotals = {} }) {
  return (
    <div className="card-recipe !p-5 mb-6" data-testid="revenue-chart">
      <p className="overline mb-3">Takings by month and plan · since launch</p>
      {series.length === 0 ? (
        <p className="text-sm text-rf-ink-soft" data-testid="revenue-chart-empty">No completed payments yet — the chart appears with your first sale.</p>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `£${v}`} tick={{ fontSize: 11 }} width={56} />
              <Tooltip formatter={(v, name) => [money(v), name]} labelFormatter={monthLabel} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {PLANS.map(([k, label, color], i) => (
                <Bar key={k} dataKey={k} name={label} stackId="rev" fill={color} radius={i === PLANS.length - 1 ? [6, 6, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="revenue-plan-totals">
        {PLANS.map(([k, label, color]) => (
          <div key={k} className="rounded-xl bg-rf-cream-alt px-3 py-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-rf-ink-soft flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {label} · all time
            </p>
            <p className="font-display text-xl font-black text-rf-ink" data-testid={`revenue-plan-total-${k}`}>{money(planTotals[k])}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DiscountReport({ rows }) {
  return (
    <div className="card-recipe !p-5 mb-6" data-testid="revenue-discounts">
      <p className="overline mb-3">Discount codes</p>
      {rows.length === 0 ? (
        <p className="text-sm text-rf-ink-soft" data-testid="revenue-discounts-empty">No discount codes yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="text-left text-xs font-black uppercase tracking-wider text-rf-ink-soft">
                <th className="py-2">Code</th><th>Off</th><th>Completed uses</th><th>Revenue</th><th>Discount given</th><th>Abandoned tries</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.code} className="border-t border-rf-ink/10" data-testid={`discount-row-${d.code}`}>
                  <td className="py-2 font-mono font-bold">{d.code}{d.active === false && <span className="ml-2 text-[10px] text-rf-ink-soft font-sans">inactive</span>}</td>
                  <td>{d.percent_off != null ? `${d.percent_off}%` : "—"}</td>
                  <td data-testid={`discount-uses-${d.code}`}>{d.uses}</td>
                  <td className="font-semibold" data-testid={`discount-revenue-${d.code}`}>{money(d.revenue)}</td>
                  <td className="text-rf-orange" data-testid={`discount-given-${d.code}`}>{money(d.discount_given)}</td>
                  <td className="text-rf-ink-soft" data-testid={`discount-abandoned-${d.code}`}>{d.abandoned}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CopyEmail = ({ email }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setDone(true);
      toast.success("Email copied");
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error("Couldn't copy — select it manually"); }
  };
  return (
    <button type="button" onClick={copy} data-testid={`expiring-copy-${email}`} className="inline-flex items-center gap-1 text-xs font-bold text-rf-orange hover:underline">
      {done ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />} {done ? "Copied" : "Copy email"}
    </button>
  );
};

export function ExpiringSoon({ rows }) {
  return (
    <div className="card-recipe !p-5 mb-6" data-testid="revenue-expiring">
      <p className="overline mb-3">Expiring in the next 14 days ({rows.length})</p>
      {rows.length === 0 ? (
        <p className="text-sm text-rf-ink-soft" data-testid="revenue-expiring-empty">No plans end in the next two weeks.</p>
      ) : (
        <ul className="divide-y divide-rf-ink/10">
          {rows.map((r) => (
            <li key={r.user_id} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`expiring-row-${r.user_id}`}>
              <span className="font-semibold min-w-[140px]">{r.name || "—"}</span>
              <span className="font-mono text-xs text-rf-ink-soft">{r.email}</span>
              <span className="text-xs">{r.plan}</span>
              <span className={`text-xs font-bold ${r.days_left <= 3 ? "text-red-600" : "text-rf-orange"}`}>
                ends {new Date(r.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {r.days_left === 0 ? "today" : `${r.days_left} day${r.days_left === 1 ? "" : "s"}`}
              </span>
              <span className="ml-auto"><CopyEmail email={r.email} /></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CSV_COLS = [
  ["name", "Name"], ["email", "Email"], ["plan", "Plan"], ["category", "Plan type"], ["kind", "Type"], ["start_date", "Start date"],
  ["end_date", "End date"], ["amount", "Amount"], ["currency", "Currency"], ["discount_code", "Discount code"],
  ["status", "Status"], ["country", "Country"], ["session_id", "Stripe session"],
];

export function downloadRevenueCsv(rows, label) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const fmt = (k, v) => (k.endsWith("_date") && v ? v.slice(0, 10) : k === "amount" ? Number(v || 0).toFixed(2) : v);
  const lines = [CSV_COLS.map(([, h]) => h).join(","), ...rows.map((r) => CSV_COLS.map(([k]) => esc(fmt(k, r[k]))).join(","))];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `recipefiler-${label}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
